/**
 * A ready refinement: the proposed changes (fields, then sections), each taken or not, sections and text fields
 * editable; the gaps and notices after them; **Apply N changes** sends the taken ones in one request and opens the
 * asset. The choices and edits are kept in this browser. A 409 keeps them: a stale revision reloads the asset to
 * apply again; changes whose text moved on the asset show its text now, still taken, for the person to review, and
 * the next apply sends that text as what they reviewed.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Eraser, TriangleAlert } from 'lucide-react';

import { isRevisionConflict, queryKeys, refineChangedIds, useApplyRefinement } from '@/api/queries';
import type { AssetOut } from '@/api/types/assets';
import type { RefineProposalOut } from '@/api/types/refinements';
import { useSession } from '@/session/SessionContext';
import { ActionButton, buttonState } from '@/ui/ActionButton';
import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { FormError } from '@/ui/StateCard';
import { useKeptDraft } from '@/ui/useKeptDraft';

import { CutNotice, GapsCard, TagNotices } from '../files/ProposalNotices';
import { fileNames } from '../files/SourceNote';
import { ChangeCard } from './ChangeCard';
import {
  applyBody,
  applyLabel,
  currentOf,
  draftFromRefinement,
  isRefineDraftOf,
  refineDraftKey,
  summaryText,
  textOf,
  withReviewed,
  type RefineDraft,
} from './refineDraft';

type Apply = ReturnType<typeof useApplyRefinement>;

/** Why the last apply did not go through, worded for what the person does next. */
function ApplyProblem({ apply, proposal, moved }: { apply: Apply; proposal: RefineProposalOut; moved: string[] }) {
  if (isRevisionConflict(apply.error)) {
    return (
      <div className="alert alert-warning form-alert" role="alert">
        <TriangleAlert aria-hidden="true" />
        <div className="a-body">
          Someone else changed this asset since you opened this page. It was reloaded and your choices are kept: apply again.
        </div>
      </div>
    );
  }
  if (refineChangedIds(apply.error).length > 0) {
    const titles = proposal.changes.filter((c) => moved.includes(c.id)).map((c) => c.title);
    return (
      <div className="alert alert-warning form-alert" role="alert">
        <TriangleAlert aria-hidden="true" />
        <div className="a-body">
          The asset changed since this refinement: {titles.join(', ')}. Their cards now show the asset&apos;s text, with your
          choices kept. Review them before you apply; nothing was changed.
        </div>
      </div>
    );
  }
  return <FormError error={apply.error} />;
}

function Toolbar({ total, taken, onAll }: { total: number; taken: number; onAll: (take: boolean) => void }) {
  return (
    <div className="row">
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => onAll(true)}>
        <Check aria-hidden="true" /> Take all
      </button>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => onAll(false)}>
        <Eraser aria-hidden="true" /> Clear
      </button>
      <span className="hint" aria-live="polite">{taken} of {total} taken</span>
    </div>
  );
}

function Changes({ asset, proposal, draft, update }: { asset: AssetOut; proposal: RefineProposalOut; draft: RefineDraft; update: (change: (d: RefineDraft) => RefineDraft) => void }) {
  const names = fileNames(proposal.files);
  const take = (id: string, on: boolean) =>
    update((d) => ({ ...d, taken: on ? proposal.changes.map((c) => c.id).filter((c) => c === id || d.taken.includes(c)) : d.taken.filter((t) => t !== id) }));
  return (
    <div className="asset-editor-sections">
      {proposal.changes.map((c) => (
        <ChangeCard
          key={c.id}
          change={c}
          current={currentOf(asset, draft, c)}
          text={textOf(draft, c)}
          taken={draft.taken.includes(c.id)}
          moved={draft.moved.includes(c.id)}
          names={names}
          onTake={(on) => take(c.id, on)}
          onText={(text) => update((d) => ({ ...d, edits: { ...d.edits, [c.id]: text } }))}
        />
      ))}
    </div>
  );
}

export function RefineReview({ asset, proposal }: { asset: AssetOut; proposal: RefineProposalOut }) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const userId = useSession().user?.id ?? 'anonymous';
  const apply = useApplyRefinement(asset.id, proposal.id);
  const { draft, update, forget } = useKeptDraft(refineDraftKey(userId, proposal.id), () => draftFromRefinement(proposal), isRefineDraftOf(proposal.id));
  const [discarding, setDiscarding] = useState(false);
  const region = useRef<HTMLElement>(null);
  const count = draft.taken.length;

  useEffect(() => region.current?.focus(), []);

  function submit() {
    if (count === 0 || apply.isPending) return;
    apply.mutate(applyBody(proposal, draft, asset.revision), {
      onSuccess: (saved) => {
        forget();
        navigate(`/assets/${saved.id}`);
      },
      onError: (error) => {
        const moved = refineChangedIds(error);
        if (moved.length === 0) return;
        update((d) => ({ ...d, moved: [...new Set([...d.moved, ...moved])] }));
        // Read the asset again and keep the text those cards now show: the next apply sends it as reviewed.
        void client.refetchQueries({ queryKey: queryKeys.asset(asset.id) }).then(() => {
          const fresh = client.getQueryData<AssetOut>(queryKeys.asset(asset.id));
          if (fresh) update((d) => withReviewed(d, proposal, fresh, moved));
        });
      },
    });
  }

  function leave() {
    forget();
    navigate(`/assets/${asset.id}`);
  }

  return (
    <div className="stack-md">
      <section ref={region} tabIndex={-1} className="stack-md" aria-labelledby="refine-results-title">
        <h2 id="refine-results-title">Proposed changes</h2>
        <p>{summaryText(proposal)}</p>
        <p className="hint">
          You asked: <q>{proposal.instruction}</q>
        </p>
        <CutNotice files={proposal.files} />
        <Toolbar total={proposal.changes.length} taken={count} onAll={(on) => update((d) => ({ ...d, taken: on ? proposal.changes.map((c) => c.id) : [] }))} />
        <Changes asset={asset} proposal={proposal} draft={draft} update={update} />
      </section>
      <TagNotices
        industries={proposal.industry_suggestions}
        partners={proposal.unknown_partners}
        source="The refinement"
        partnersNote="Taking the Partners change adds them as new partners."
      />
      <GapsCard gaps={proposal.gaps} id="refine-gaps-title" />
      <div className="card">
        <div className="card-body stack-md">
          <ApplyProblem apply={apply} proposal={proposal} moved={draft.moved} />
          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => (count > 0 ? setDiscarding(true) : leave())}>Discard</button>
            <ActionButton type="button" state={buttonState(apply)} label={applyLabel(count)} pendingLabel="Applying…" doneLabel="Applied" disabled={count === 0} onClick={submit} />
          </div>
        </div>
      </div>
      {discarding && (
        <ConfirmDialog title="Discard this refinement?" confirmLabel="Discard" danger onClose={() => setDiscarding(false)} onConfirm={leave}>
          <p>Nothing is applied and the asset stays as it is. Your choices on this page are dropped.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
